import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { createAppThunk, type IRes } from "store/modules/common";
import Http, { HttpCm } from "commons/Http";
import type { IPageInfo } from "pages/notice/store/NoticeVo";
import { ActiveType } from "pages/calculate/store/CalculateVo";
import {
  ChannelStatusType,
  type IFetchStatusChange,
  type IChannelServiceDetailVo,
  type IResServiceChanneList,
  type IRssListVo,
  type IServicChannelInfoVo,
  type ServiceActiveTab,
  type ServiceSubTab,
  type IConsaladChannelMusicGridVo,
  type IServiceMediaChannelInfo,
  type IRssCategorySelectboxListVo,
  type IRssUsingFlagParam,
  RSSstatusType,
  type IRssPopupInfoVo,
  type IResRssInfo,
} from "./ServiceVo";
import { type IServiceStatusInfoListDo, type ServiceType, type IResServiceInfo } from "pages/entry/store/entryVo";
import { uiAction } from "commons/ui/useUihook";
import { UiType } from "commons/ui/uiVo";
import i18n from "i18n";

// 이름 설정
const name = "service";

export interface IService {
  activeTab: ServiceActiveTab;
  subTab: string;

  rejectReason?: string;
  //채널관리 리스트 (콘텐츠)
  channel_manage: {
    list: IServicChannelInfoVo[];
    status?: ChannelStatusType;
    page_info?: IPageInfo;
    detail?: IChannelServiceDetailVo;
  };

  //채널관리 리스트 (음원)
  media_manage: {
    page_info?: IPageInfo;
    list: IServiceMediaChannelInfo[];
  };

  //회원 가입 후 또는 로그인 후 서비스 없을 경우 신청 관련 상태값
  info: {
    service_list: { [key: string]: IServiceStatusInfoListDo };
    view_step: number;
    select_type?: ServiceType;
    applyLoading?: boolean;
  };

  rss: {
    id?: number;
    channel_code?: string;
    channel_name?: string;
    auto_filter_flag?: boolean;
    trends_flag?: boolean;
    list?: IRssListVo[];
    status?: ChannelStatusType;
    detail?: IRssListVo;
    popup?: IRssPopupInfoVo;
  };
  music?: {
    pageInfo?: IPageInfo;
    list?: IConsaladChannelMusicGridVo[];
  };
}

//서비스 신청 단계, 서비스 신청 정보 확인
export const apiGserviceInfoApply = createAppThunk<IResServiceInfo[], undefined>(
  `${name}/apiGserviceInfoApply`,
  async (_, thunkApi) => {
    const res = await Http.get<IRes<IResServiceInfo[]>>("service/apply/status");
    if (res.data.content) {
      thunkApi.dispatch(rdxSetServiceApplyInitStatus(res.data.content));
    }
    return res.data;
  },
);

// [콘텍스트] 서비스 신청 1단계, 검토 신청 버튼 누르면 실행 할 API (서비스 등록)
export const apiPserviceContextRegist = createAppThunk<undefined, boolean>(
  `${name}/apiPserviceContextRegist`,
  async (re_flag, thunkApi) => {
    const param = {
      id: re_flag && thunkApi.getState().service.channel_manage.detail?.id,
      url: thunkApi.getState().ui.inputText?.["entry_website"]?.trim(),
      desc: thunkApi.getState().ui.inputText?.["entry_brand_info"]?.trim(),
      name: thunkApi.getState().ui.inputText?.["entry_site_name"]?.trim(),
      lang: thunkApi.getState().ui.selectbox?.["service_language_type"],
    };
    const res = await Http.post("service/apply/provider", param);

    return res.data;
  },
);

// [음원] 서비스 신청 1단계, 검토 신청 버튼 누르면 실행 할 API (서비스 등록)
export const apiPserviceMediaRegist = createAppThunk<undefined, boolean>(
  `${name}/apiPserviceMediaRegist`,
  async (re_flag, thunkApi) => {
    const param = {
      id: re_flag && thunkApi.getState().service.channel_manage.detail?.id,
      url: thunkApi.getState().ui.inputText?.["entry_youtube"]?.trim(),
      desc: thunkApi.getState().ui.inputText?.["entry_brand_info"]?.trim(),
      name: thunkApi.getState().ui.inputText?.["entry_youtube_name"]?.trim(),
      ucid: thunkApi.getState().ui.inputText?.["entry_youtube_id"]?.trim(),
    };
    const res = await Http.post("service/apply/media", param);

    return res.data;
  },
);

// [음원] 채널 삭제 API
export const apiPserviceMediaDelete = createAppThunk<undefined, number>(`${name}/apiPserviceMediaDelete`, async id => {
  const param = {
    id: id,
  };

  const res = await Http.post("service/apply/media/delete", param);
  return res.data;
});

//서비스 신청 단계 마무리! 서비스 시작 버튼 클릭 시 API
export const apiPserviceStart = createAppThunk<undefined, number>(`${name}/apiPserviceStart`, async id => {
  const res = await Http.post("service/apply/start", { id: id });
  return res.data;
});

/**
 * 서비스 > 채널 관리 정보 리스트 출력
 * 콘텐츠, 음원/영상 탭 상관없이 다 여기서 !!! 리스트 불러옵네다!!!!
 */
export const apiGChannelCpList = createAppThunk<
  IResServiceChanneList<IServicChannelInfoVo | IServiceMediaChannelInfo>,
  { page: number; name?: string; code?: string; state?: string; type: ServiceActiveTab }
>(`${name}/apiGChannelCpList`, async (param, thunkApi) => {
  const type = param.type.toLocaleLowerCase();
  const params = {
    // id: param.code ? param.code : undefined,
    id: thunkApi.getState().navigates.selectChannel ? thunkApi.getState().navigates.selectChannel?.value : undefined,
    name: param.name && param.name.length > 0 ? param.name : undefined,
    page: param.page !== undefined ? param.page : thunkApi.getState().service.channel_manage.page_info,
    size: thunkApi.getState().service.channel_manage.page_info
      ? thunkApi.getState().service.channel_manage.page_info!.size
      : 20,
    status: thunkApi.getState().service.channel_manage.status
      ? thunkApi.getState().service.channel_manage.status
      : thunkApi.getState().ui?.selectbox?.[`service_${type}_channel_status`],

    // status: param.state ? param.state : undefined,
  };
  const res = await Http.get<IRes<IResServiceChanneList<IServicChannelInfoVo | IServiceMediaChannelInfo>>>(
    `service/apply/${type}`,
    {
      params: params,
      headers: { "Accept-Language": i18n.language },
    },
  );

  const returnList = (list: Array<any>) => {
    return type === ActiveType.CP ? (list as IServicChannelInfoVo[]) : (list as IServiceMediaChannelInfo[]);
  };

  if (res.data.content) {
    thunkApi.dispatch(
      rdxSetChannelGridList({ type: type.toLocaleUpperCase(), data: returnList(res.data.content.list) }),
    );
    thunkApi.dispatch(
      rdxSetChannelGridPageInfo({
        type,
        info: {
          page: res.data.content.page_info.page,
          size: res.data.content.page_info.size,
          total_elements: res.data.content.page_info.total_elements,
          totalPage: Math.ceil(res.data.content.page_info.total_elements! / res.data.content.page_info.size!),
        },
      }),
    );
  }
  return res.data;
});
// 관리자 일 경우! > 서비스 상태 변경 API
export const apiPserviceStatusChange = createAppThunk<undefined, IFetchStatusChange>(
  `${name}/apiPserviceStatusChange`,
  async (param, thunkApi) => {
    const type = thunkApi.getState().service.activeTab;
    const res = await Http.post("service/status", param);
    if (res.data.content) {
      if (type === "PROVIDER") {
        thunkApi.dispatch(rdxSetChannelListStatusChange(param));
      } else {
        thunkApi.dispatch(rdxSetMediaListStatusChange(param));
      }

      // 채널 리스트 중 toggle 버튼으로 작동한 아이들이 아닐 경우!
      if (param.status !== ChannelStatusType.service && param.status !== ChannelStatusType.stop) {
        thunkApi.dispatch(rdxSetChannelPopupStatusChange(param.status as ChannelStatusType));
      }
    }
    return res.data;
  },
);

//서비스 > 채널 관리 > 채널 상세 정보 조회 ( 서비스 팝업 )
export const apiGChannelServiceInfoDetail = createAppThunk<
  IChannelServiceDetailVo,
  { id: number; type: ServiceActiveTab; entry?: boolean }
>(`${name}/apiGChannelServiceInfoDetail`, async (param, thunkApi) => {
  const type = param.entry
    ? thunkApi.getState().service.info!.select_type!.toLocaleLowerCase()
    : param.type.toLocaleLowerCase();

  const res = await Http.get<IRes<IChannelServiceDetailVo>>(`service/apply/${type}/${param.id}`, {
    params: { id: param.id },
    headers: { "Accept-Language": i18n.language },
  });
  if (res.data.content) {
    thunkApi.dispatch(rdxSetChannelServiceDetail(res.data.content));
  }
  return res.data;
});

// 📌 여기부터 RSS

//RSS 리스트 출력
export const apiGcpRssList = createAppThunk<IResRssInfo, undefined>(`${name}/apiGcpRssList`, async (_, thunkApi) => {
  const code = thunkApi.getState().service.rss.channel_code;
  const rssSelectType = thunkApi.getState().ui?.selectbox?.["rss-type-selectbox"]
    ? thunkApi.getState().ui?.selectbox?.["rss-type-selectbox"] === "all"
      ? undefined
      : thunkApi.getState().ui?.selectbox?.["rss-type-selectbox"]
    : undefined;
  const res = await Http.get<IRes<IResRssInfo>>("cp-endpoint/list", {
    params: {
      content_provider_code: code,
      cp_endpoint_type: rssSelectType,
    },
  });
  if (res.data.content) {
    thunkApi.dispatch(rdxSetRSSgridList(res.data.content.list));
    thunkApi.dispatch(
      rdxSetRSSDistributionFlag({ auto: res.data.content.auto_filter_flag, trend: res.data.content.trends_flag }),
    );
  }
  return res.data;
});

//RSS 등록 팝업 > RSS 연동 카테고리 select box list load
export const apiGrssCategorySelectboxList = createAppThunk<{ list: IRssCategorySelectboxListVo[] }, string>(
  `${name}/apiGrssCategorySelectboxList`,
  async (type, thunkApi) => {
    const res = await Http.get<IRes<{ list: IRssCategorySelectboxListVo[] }>>("cp-endpoint/category/select-box", {
      params: { cp_endpoint_type: type },
    });
    if (res.data.content) {
      thunkApi.dispatch(rdxSetRSScategoryList({ type: type, list: res.data.content.list }));
    }
    return res.data;
  },
);

/**
 * RSS 등록 (RSS 검토 신청 단계)
 * @param {string} type method type : INSERT | UPDATE
 */
export const apiPrssRegister = createAppThunk<undefined, string>(`${name}/apiPrssRegister`, async (type, thunkApi) => {
  // method: "INSERT" : 새로 등록
  // method: "UPDATE" : 반려 후 수정 후 재신청
  // method가 "UPDATE" 인 경우엔 cp_endpoint_id, cp_category_mapping_id에 값이 있고, 새로 등록하는 경우엔 undefined 입니다.

  const checkType = thunkApi.getState().ui.checkBox?.["rss_platform_checkbox"];
  const checkList: string[] = [];

  if (checkType) {
    for (const [key, value] of Object.entries(checkType)) {
      if (value) {
        checkList.push(key);
      }
    }
  }
  const param = {
    method: type.toUpperCase(),

    content_provider_id: thunkApi.getState().service.rss.id,
    content_provider_code: thunkApi.getState().service.rss.channel_code,

    cp_endpoint_id: thunkApi.getState().service.rss.detail?.cp_endpoint_id,
    cp_category_mapping_id: thunkApi.getState().service.rss.detail?.cp_category_mapping_id,

    cp_endpoint_type_list: checkList,
    crawler_type: thunkApi.getState().ui.radioBox?.["rss_viewus_type_select"]
      ? thunkApi.getState().ui.radioBox?.["rss_viewus_type_select"]
      : null,

    viewus_link: thunkApi.getState().ui.inputText?.["rss_url_RSS"]?.trim(),
    viewus_last_cp_category_name: thunkApi.getState().ui.inputText?.["rss_category_RSS"]?.trim(),
    viewus_last_cp_category_code: thunkApi.getState().ui.inputText?.["rss_category_code_RSS"]?.trim(),
    viewus_category_id: Number(thunkApi.getState().ui.selectbox?.["rss_mapping_category_RSS"]),

    msn_link: thunkApi.getState().ui.inputText?.["rss_url_MSN"]?.trim(),
    msn_last_cp_category_name: thunkApi.getState().ui.inputText?.["rss_category_MSN"]?.trim(),
    msn_last_cp_category_code: thunkApi.getState().ui.inputText?.["rss_category_code_MSN"]?.trim(),
    msn_category_id: Number(thunkApi.getState().ui.selectbox?.["rss_mapping_category_MSN"]),
  };

  const res = await Http.post("cp-endpoint/save", param);

  if (res.data) {
    if (res.data.code === 200) {
      //수정 후 재 신청 일 경우
      if (thunkApi.getState().service.rss.detail) {
        thunkApi.dispatch(
          rdxSetRSSdetailInfoChange({
            id: thunkApi.getState().service.rss.detail!.cp_category_mapping_id!,
            status: RSSstatusType.await,
          }),
        );
      }
      //  else {
      //   //새 신청일 경우
      //   thunkApi.dispatch(rdxSetRSSdetailInitSet({ ...param, inspect_status: RSSstatusType.await }));
      // }
    }
  }
  return res.data;
});

// 관리자 일 경우! > RSS 연결 등록 - 해제 (using flag)
export const apiPrssStatusChange = createAppThunk<undefined, IRssUsingFlagParam>(
  `${name}/apiPrssStatusChange`,
  async (param, thunkApi) => {
    const res = await Http.post("cp-endpoint/using-flag/save", param);

    if (res.data.content) {
      thunkApi.dispatch(rdxSetRssListStatusChange(param));
    }

    return res.data;
  },
);

// 관리자 일 경우! > RSS 검토 시 RSS 검증 테스트 하는 API (RSS 검수)
export const apiPrssInspectionCheck = createAppThunk(`${name}/apiPrssInspectionCheck`, async (_, thunkApi) => {
  const param = {
    cp_endpoint_id: thunkApi.getState().service.rss.detail?.cp_endpoint_id,
    cp_category_mapping_id: thunkApi.getState().service.rss.detail?.cp_category_mapping_id,
    inspect_item_count: 1,
  };
  const res = await HttpCm.post("/api/contents/inspect", param);
  if (res.data) {
    thunkApi.dispatch(rdxSetRSSinspectFlagSet(true));
  }

  return res.data;
});

// 관리자 일 경우! >  테스트(RSS 검수)가 완료 되면 알림톡 관련하여 API 쏴줌
export const apiPrssInspectAlarm = createAppThunk(`${name}/apiPrssInspectAlarm`, async (_, thunkApi) => {
  const res = await Http.get("admin/alarm/inspect", {
    params: { content_provider_code: thunkApi.getState().service.rss.channel_code },
  });

  return res.data;
});

// 관리자 일 경우! > RSS 서비스 상태 변경 API
export const apiPrssServiceStatusChange = createAppThunk<undefined, RSSstatusType>(
  `${name}/apiPserviceStatusChange`,
  async (param, thunkApi) => {
    const res = await Http.post("cp-endpoint/inspect-status/save", {
      cp_endpoint_id: thunkApi.getState().service.rss.detail?.cp_endpoint_id,
      cp_category_mapping_id: thunkApi.getState().service.rss.detail?.cp_category_mapping_id,
      inspect_status: param,
      message: thunkApi.getState().service.rejectReason && thunkApi.getState().service.rejectReason?.trim(),
    });
    if (res.data) {
      if (res.data.code === 200) {
        thunkApi.dispatch(
          rdxSetRSSdetailInfoChange({
            id: thunkApi.getState().service.rss!.detail!.cp_category_mapping_id!,
            status: param,
            message: thunkApi.getState().service.rejectReason?.trim(),
          }),
        );
      }
    }
    return res.data;
  },
);

//❗️콘샐러드
/**
 * 콘셀러드: 콘텐츠분석 > 음원목록 그리드 조회
 */
export const apiGmusicList = createAppThunk(`${name}/apiGmusicList`, async (option: { v?: number }, thunkApi) => {
  const res = await Http.get("/consalad/analyze/soundTrack", {
    headers: { "Accept-Language": i18n.language },
    params: {
      page: option.v ? option.v : 0,
      size: 20,
      tagId:
        thunkApi.getState().ui?.selectbox?.["tagList"] && Number(thunkApi.getState().ui!.selectbox!["tagList"]) > 0
          ? thunkApi.getState().ui.selectbox?.["tagList"]
          : null,
    },
  });

  if (res) {
    if (res.data.content.data.page_info.page > 0) {
      thunkApi.dispatch(
        rdxSetMusicGridData(thunkApi.getState().service.music!.list!.concat(res.data.content.data.list)),
      );
    } else {
      thunkApi.dispatch(rdxSetMusicGridData(res.data.content.data.list));
    }

    if (res.data?.content?.data.page_info) {
      thunkApi.dispatch(
        rdxConsaladMusicPageInfo({
          page: res.data.content.data.page_info!.page + 1,
          size: res.data.content.data.page_info!.size,
          total_elements: res.data.content.data.page_info!.total_elements,
          totalPage: Math.ceil(res.data.content.data.page_info!.total_elements / res.data.content.data.page_info!.size),
        }),
      );
    }
  }

  return res.data;
});

/**
 * 콘셀러드: 콘텐츠분석 > 음원목록 [음원불러오기]
 */
export const apiPmusicUpload = createAppThunk(`${name}/apiPmusicUpload`, async (_, thunkApi) => {
  const form = new FormData();

  form.append(
    "coverFile",
    thunkApi.getState().ui!.inputFile!["albumCover"].file!,
    thunkApi.getState().ui!.inputFile!["albumCover"].file!.name!,
  );
  form.append(
    "soundFile",
    thunkApi.getState().ui!.inputFile!["music"].file!,
    thunkApi.getState().ui!.inputFile!["music"].file!.name!,
  );
  form.append("isrc", thunkApi.getState().ui!.inputText!["isrc"]!);
  form.append("track", thunkApi.getState().ui!.inputText!["track"]!);
  form.append("tags", thunkApi.getState().ui!.inputText!["tags"]!);
  // form.append("limit_time", thunkApi.getState().ui!.inputText!["limitTime"]!);

  const res = await Http.post("/consalad/analyze/soundTrack", form, {
    headers: { "content-type": "multipart/form-data" },
  });

  return res.data;
});

/**
 * 콘셀러드: 콘텐츠분석 > 음원목록 "분위기" 셀렉트박스 데이터
 */
export const apiGmusicTagcodebook = createAppThunk(`${name}/apiGmusicTagcodebook`, async (_, thunkApi) => {
  const res = await Http.get("/codebook/consalad/soundTrack/tag", { headers: { "Accept-Language": i18n.language } });

  if (res) {
    const { add, commit } = uiAction(thunkApi);

    add(
      UiType.CODE_BOOK,
      "tagList",
      [{ id: 0, name: i18n.t("media.musicList.all") }].concat(
        res.data?.content.map((item: { id: number; name: string }) => ({ ...item, name: `#${item.name}` })),
      ),
    );
    commit();
  }
  return res.data;
});

/**
 * 콘셀러드: 콘텐츠분석 > 을원목록 등록한 음원 삭제
 */
export const apiPdeleteMusicItem = createAppThunk(`${name}/apiPdeleteMusicItem`, async (id: number) => {
  const res = await Http.post(`/consalad/analyze/soundTrack/${id}`);

  return res.data;
});

// 콘텍스트 > 채널관리 > 예상 단가 설정 / 트렌드 배포 설정 / 자동 배포 설정
export const apiPchannelProviderSetting = createAppThunk<undefined, { id?: number; auto?: boolean; trends?: boolean }>(
  `${name}/apiPchannelProviderSetting`,
  async (params, thunkApi) => {
    const price = thunkApi.getState().ui.inputText?.["popup_unit_price_val"]?.trim();
    const id = params.id ? params.id : thunkApi.getState().service.rss.id ? thunkApi.getState().service.rss.id : -1;
    const param = {
      id: id,
      expected_unit_price: price ? price : undefined,
      trends_flag: params.trends,
      auto_filter_flag: params.auto,
    };
    const res = await Http.post("service/apply/provider/setting", param);
    if (res.data.content) {
      if (price !== undefined) {
        thunkApi.dispatch(
          rdxSetChannelPriceChange({
            id: id!,
            price: Number(price),
          }),
        );
      }
      if (params.auto !== undefined || params.trends !== undefined) {
        thunkApi.dispatch(
          rdxSetRSSDistributionFlag({
            auto: params.auto !== undefined ? params.auto : undefined,
            trend: params.trends !== undefined ? params.trends : undefined,
          }),
        );
      }
    }

    return res.data;
  },
);

const ServiceSlice = createSlice({
  name,
  initialState: {
    activeTab: "PROVIDER",
    subTab: "channel",
    channel_manage: {
      list: [],
    },
    media_manage: {
      list: [],
    },
    //회원 가입 후 서비스 신청 관련 리덕스
    info: {
      view_step: 0,
      service_list: {
        PROVIDER: {
          // status: ChannelStatusType.await,
        },
        MEDIA: {},
      },
    },
    music: {
      pageInfo: {
        page: 0,
        size: 20,
        totalPage: 0,
        totalElement: 0,
      },
    },
    rss: {},
  } as IService,
  reducers: {
    rdxSetActiveTab(state: IService, action: PayloadAction<ServiceActiveTab>) {
      state.activeTab = action.payload;
      state.subTab = "channel";
    },
    rdxSetSubTab(state: IService, action: PayloadAction<ServiceSubTab>) {
      state.subTab = action.payload;
    },
    rdxSetServiceRejectReason(state: IService, action: PayloadAction<string | undefined>) {
      state.rejectReason = action.payload;
    },

    //❗️서비스 신청 관련
    rdxSetServiceApplyInitStatus(state: IService, action: PayloadAction<IResServiceInfo[]>) {
      if (action.payload.length > 0) {
        action.payload.map(i => {
          state.info!.service_list[i.type].status = i.status;
          state.info!.service_list[i.type].id = i.id;
          // switch (i.status) {
          //   case ChannelStatusType.await:
          //   case ChannelStatusType.inspect:
          //   case ChannelStatusType.reject:
          //     state.info.service_list[i.type].step = 2;
          //     break;
          //   case ChannelStatusType.approval:
          //   case ChannelStatusType.service:
          //     state.info.service_list[i.type].step = 3;
          //     break;
          //   default:
          //     state.info.service_list[i.type].step = 0;
          // }
        });
      }
    },
    //회원가입 후 서비스 신청 단계
    rdxSetServiceApplyStep(state: IService, action: PayloadAction<number>) {
      state.info!.view_step = action.payload;
    },
    //회원가입 후 서비스 신청 타입(콘텍스트, 음원)
    rdxSetServiceApplyType(state: IService, action: PayloadAction<ServiceType | undefined>) {
      state.info!.select_type = action.payload;
    },
    //회원가입 후 서비스 반려 이후 재신청 flag
    rdxSetServiceApplyReStatus(
      state: IService,
      action: PayloadAction<{ type: ServiceType; flag: boolean; popupFlag?: boolean }>,
    ) {
      if (action.payload.popupFlag) {
        state.channel_manage.detail!.re_flag = action.payload.flag;
      } else {
        state.info!.service_list[action.payload.type].re_flag = action.payload.flag;
      }
    },

    //❗️채널리스트 관련

    // 채널 그리드 리스트
    rdxSetChannelGridList(
      state: IService,
      action: PayloadAction<{ type: string; data: IServicChannelInfoVo[] | IServiceMediaChannelInfo[] }>,
    ) {
      const { type, data } = action.payload;
      if (type === ActiveType.CP) {
        state.channel_manage.list = data as IServicChannelInfoVo[];
      } else {
        state.media_manage.list = data as IServiceMediaChannelInfo[];
      }
    },

    rdxSetChannelGridPageInfo(state: IService, action: PayloadAction<{ type: string; info: IPageInfo }>) {
      const { type, info } = action.payload;
      if (type.toLocaleUpperCase() === ActiveType.CP) {
        state.channel_manage.page_info = info;
      } else {
        state.media_manage.page_info = info;
      }
    },

    // 채널 그리드 리스트 에서 서비스 상태값 변경했을 때 그 상태만 변경하기 위한 값
    rdxSetChannelListStatusChange(state: IService, action: PayloadAction<IFetchStatusChange>) {
      state.channel_manage.list.map(i => {
        if (i.id === action.payload.id) {
          i.status = action.payload.status as ChannelStatusType;
        }
      });
      if (state.channel_manage.detail && state.channel_manage.detail.admin_status) {
        delete state.channel_manage.detail.admin_status;
      }
    },
    // 채널 서비스 신청 팝업에서  서비스 상태값 변경했을 때 그 상태만 변경하기 위한 값
    rdxSetChannelPopupStatusChange(state: IService, action: PayloadAction<ChannelStatusType>) {
      state.channel_manage.detail!.status = action.payload;
    },

    // 사용자가 신청한 서비스 상태 디테일
    rdxSetChannelServiceDetail(state: IService, action: PayloadAction<IChannelServiceDetailVo>) {
      state.channel_manage.detail = action.payload;
    },

    // 관리자 일 경우 진짜 상태값 변경하기전 임시 상태값 가지고 있기 위해
    rdxSetChannelServiceAdminDetailStatus(state: IService, action: PayloadAction<ChannelStatusType>) {
      state.channel_manage.detail!.admin_status = action.payload;
    },

    //채널관리 서비스상태 셀렉트박스 값 저장
    rdxSetSelectServiceStatus(state: IService, action: PayloadAction<string | number | undefined>) {
      state.channel_manage.status = action.payload ? (action.payload as ChannelStatusType) : undefined;
    },

    //채널 그리드 리스트에서 단가 숫자만 변경하기 위해
    rdxSetChannelPriceChange(state: IService, action: PayloadAction<{ id: number; price: number }>) {
      state.channel_manage.list.map(i => {
        if (i.id === action.payload.id) {
          i.expected_unit_price = action.payload.price;
        }
      });
    },

    //❗️RSS 관련

    //선택된 채널 정보 RSS쪽에 저장
    rdxSetRSSchannelInfo(
      state: IService,
      action: PayloadAction<{ id: number; name: string; code: string; status: ChannelStatusType }>,
    ) {
      state.rss = {
        id: action.payload.id,
        channel_code: action.payload.code,
        channel_name: action.payload.name,
        status: action.payload.status,
      };
    },

    //서비스 시작 / 중지 플래그
    rdxSetRSSchannelStatusChange(state: IService, action: PayloadAction<ChannelStatusType>) {
      state.rss.status = action.payload;
    },

    // RSS 그리드  리스트
    rdxSetRSSgridList(state: IService, action: PayloadAction<IRssListVo[]>) {
      state.rss!.list = action.payload;
    },
    // 채널 배포 관련 플래그 상태값
    rdxSetRSSDistributionFlag(state: IService, action: PayloadAction<{ auto?: boolean; trend?: boolean }>) {
      if (action.payload.auto !== undefined) {
        state.rss!.auto_filter_flag = action.payload.auto;
      }
      if (action.payload.trend !== undefined) {
        state.rss!.trends_flag = action.payload.trend;
      }
    },

    // RSS 연동 카테고리 리스트 (셀렉트박스)
    rdxSetRSScategoryList(
      state: IService,
      action: PayloadAction<{ type: string; list: IRssCategorySelectboxListVo[] }>,
    ) {
      const list = action.payload.list.map(i => {
        return { id: i.category_id, name: i.category_name, value: i.category_id };
      });
      state.rss.popup = {
        ...state.rss.popup,
        categorySelectList: { ...state.rss.popup?.categorySelectList, [action.payload.type]: list },
      };
    },
    // RSS 상태 클릭 시 RSS 상세 정보 리덕스 저장
    rdxSetRSSdetailInfo(state: IService, action: PayloadAction<number>) {
      state.rss.detail = state.rss!.list!.filter(i => i.cp_category_mapping_id === action.payload)[0];
    },
    // detail의 상태값 변경
    rdxSetRSSdetailInfoChange(
      state: IService,
      action: PayloadAction<{ id: number; status: RSSstatusType; message?: string }>,
    ) {
      state.rss!.list!.map(i => {
        if (i.cp_category_mapping_id === action.payload.id) {
          i.inspect_status = action.payload.status;
        }
      });

      state.rss.detail!.inspect_status = action.payload.status;
      state.rss.detail!.message = action.payload.message;
      delete state.rss.popup?.re_flag;
    },

    // rss 새로 등록 시 detail값 설정해주기
    rdxSetRSSinspectFlagSet(state: IService, action: PayloadAction<boolean>) {
      state.rss.detail!.inspect_apply_flag = action.payload;
    },

    // rss 새로 등록 시 detail값 설정해주기
    rdxSetRSSdetailInitSet(state: IService, action: PayloadAction<IRssListVo>) {
      state.rss.detail = action.payload;
    },

    // 관리자 일 경우 반려, 승인 관련 상태 변경
    rdxSetAdminStatusChange(state: IService, action: PayloadAction<RSSstatusType>) {
      const status = action.payload === RSSstatusType.fail ? RSSstatusType.reject : RSSstatusType.approval;
      state.rss.popup = {
        ...state.rss.popup,
        admin_status: status,
      };
    },
    // 사용자 일 경우 반려 > 수정 후 재신청 일 경우
    rdxSetUserReApplyStatusChange(state: IService, action: PayloadAction<boolean>) {
      state.rss.popup = {
        ...state.rss.popup,
        re_flag: action.payload,
      };
    },
    // 관리자 일 경우 rss 정보 수정 / 테스트 버튼 상태
    rdxSetAdminRssModifyChange(state: IService, action: PayloadAction<string>) {
      state.rss.popup = {
        ...state.rss.popup,
        state: action.payload,
      };
    },
    // RSS 그리드 리스트 에서 서비스 상태값 변경했을 때 그 상태만 변경하기 위한 값
    rdxSetRssListStatusChange(state: IService, action: PayloadAction<IRssUsingFlagParam>) {
      state.rss!.list!.map(i => {
        if (i.cp_category_mapping_id === action.payload.cp_category_mapping_id) {
          i.cp_category_mapping_using_flag = action.payload.cp_category_mapping_using_flag;
          i.inspect_status = action.payload.cp_category_mapping_using_flag
            ? ("SERVICE" as RSSstatusType)
            : ("STOP" as RSSstatusType);
        }
      });
    },

    //❗️음원 관련
    rdxSetMusicGridData(
      state: IService,
      action: PayloadAction<
        {
          id: number;
          cover_path: string;
          isrc: string;
          name: string;
          tag_list: {
            id: number;
            name: string;
          }[];
          sound_path: string;
        }[]
      >,
    ) {
      state.music!.list = action.payload.map(i => ({ ...i, playFlag: false }));
    },
    rdxConsaladMusicPageInfo(state: IService, action: PayloadAction<IPageInfo>) {
      state.music!.pageInfo = action.payload;
    },
    rdxFlagChange(state: IService, action: PayloadAction<number>) {
      state.music!.list![action.payload].playFlag = true;
    },
    rdxRemoveMediaList(state: IService, action: PayloadAction<number>) {
      const removeList = state.media_manage.list.filter(item => item.id !== action.payload);
      state.media_manage.list = removeList;
    },
    rdxSetMediaListStatusChange(state: IService, action: PayloadAction<IFetchStatusChange>) {
      const channelId = state.channel_manage!.detail?.ucid !== null ? state.channel_manage.detail?.ucid : "";
      state.media_manage.list.map(i => {
        if (i.id === action.payload.id) {
          i.channel_id = channelId ? channelId : ("" as string);
          i.status = action.payload.status as ChannelStatusType;
        }
      });
      if (state.channel_manage.detail && state.channel_manage.detail.admin_status) {
        delete state.channel_manage.detail.admin_status;
      }
    },

    //❗️리덕스 초기화

    //대분류 탭 초기화
    rdxResetActiveTab(state: IService) {
      state.activeTab = ActiveType.CP;
    },
    //rss 정보 삭제
    rdxResetRSSInfo(state: IService) {
      // delete state.rss;
    },
    //서비스 정보 리셋
    rdxResetServiceInfo(state: IService) {
      state.info = {
        view_step: 0,
        service_list: {
          PROVIDER: {},
          MEDIA: {},
        },
      };
    },
    //서비스 탭 > 채널 정보 삭제
    rdxResetServiceChannelInfo(state: IService) {
      state.channel_manage.list = [];
      delete state.channel_manage.page_info;
      delete state.channel_manage.status;
    },
    // 사용자가 신청한 서비스 상태 디테일 삭제
    rdxReSetChannelServiceDetail(state: IService) {
      delete state.channel_manage.detail;
      delete state.rejectReason;
    },

    // 팝업 닫을때 rss detail 삭제
    rdxReSetRssDetail(state: IService) {
      delete state.rss.detail;
      delete state.rss.popup;
      delete state.rejectReason;
      delete state.channel_manage.detail;
    },
  },
  extraReducers: builder => {
    builder.addCase(apiPserviceContextRegist.pending, state => {
      state.info.applyLoading = true;
    });
    builder.addCase(apiPserviceContextRegist.fulfilled, state => {
      state.info.applyLoading = false;
    });
    builder.addCase(apiPserviceMediaRegist.pending, state => {
      state.info.applyLoading = true;
    });
    builder.addCase(apiPserviceMediaRegist.fulfilled, state => {
      state.info.applyLoading = false;
    });
    builder.addCase(apiPserviceStart.pending, state => {
      state.info.applyLoading = true;
    });
    builder.addCase(apiPserviceStart.fulfilled, state => {
      state.info.applyLoading = false;
    });

    builder.addCase(apiPrssRegister.pending, state => {
      state.rss.popup = { ...state.rss.popup, loading: true };
    });
    builder.addCase(apiPrssRegister.fulfilled, state => {
      state.rss.popup = { ...state.rss.popup, loading: false };
    });
    builder.addCase(apiPrssServiceStatusChange.pending, state => {
      state.rss.popup = { ...state.rss.popup, loading: true };
    });
    builder.addCase(apiPrssServiceStatusChange.fulfilled, state => {
      state.rss.popup = { ...state.rss.popup, loading: false };
    });
  },
});

// 리덕스 액션
export const {
  rdxSetActiveTab,
  rdxSetSubTab,
  rdxSetServiceRejectReason,

  // 서비스 초기 상태 관련
  rdxSetServiceApplyInitStatus,
  rdxSetServiceApplyStep,
  rdxSetServiceApplyType,
  rdxSetServiceApplyReStatus,

  //채널 관련
  rdxSetChannelGridList,
  rdxSetChannelGridPageInfo,
  rdxSetChannelListStatusChange,
  rdxSetChannelServiceDetail,
  rdxSetChannelPopupStatusChange,
  rdxSetChannelServiceAdminDetailStatus,
  rdxSetSelectServiceStatus,
  rdxSetChannelPriceChange,

  //RSS 관련
  rdxSetRSSchannelInfo,
  rdxSetRSSchannelStatusChange,
  rdxSetRSSgridList,
  rdxSetRSScategoryList,
  rdxSetRSSdetailInfo,
  rdxSetRssListStatusChange,
  rdxSetAdminStatusChange,
  rdxSetUserReApplyStatusChange,
  rdxSetRSSdetailInfoChange,
  rdxSetRSSdetailInitSet,
  rdxSetRSSinspectFlagSet,
  rdxSetAdminRssModifyChange,
  rdxSetRSSDistributionFlag,

  //음원 관련
  rdxSetMusicGridData,
  rdxConsaladMusicPageInfo,
  rdxFlagChange,
  rdxRemoveMediaList,
  rdxSetMediaListStatusChange,

  //초기화 관련
  rdxResetActiveTab,
  rdxResetRSSInfo,
  rdxResetServiceInfo,
  rdxResetServiceChannelInfo,
  rdxReSetChannelServiceDetail,
  rdxReSetRssDetail,
} = ServiceSlice.actions;

// 리덕스 리듀서
export default ServiceSlice.reducer;
